import { get } from "~/lib/lodash";
import { createAbility } from "~/services/user/caslPermissions";
import { type PermissionsParams } from "~/services/user/permissions/authenticationOptions";
import { Action, Subject } from "~/services/user/permissions/caslPermissionsType";
import { getUserRoles } from "~/services/user/permissions/utils/getUserRoles";

export const canAccessCompensationReview = (params: PermissionsParams): boolean => {
  const { user } = params;
  if (!user?.company.hasImportedEmployees) {
    return false;
  }

  return createAbility(user, params).can(Action.Access, Subject.CompensationReview);
};

export const canAccessSalaryBands = (params: PermissionsParams): boolean => {
  const { user } = params;

  if (!user?.company.hasImportedEmployees) {
    return false;
  }

  if (getUserRoles(user).isAdmin) {
    // bands aren't shared explicitly, so we need to check if the user is an admin to give access
    return createAbility(user, params).can(Action.Access, Subject.SalaryBands);
  }

  const hasSharedSalaryBands = get(params.permissionsSchema.SalaryBand.read, "id.in", []).length > 0;
  return hasSharedSalaryBands && createAbility(user, params).can(Action.Access, Subject.SalaryBands);
};

export const canAccessComptryxMapping = (params: PermissionsParams): boolean => {
  const { subscriptions, featureFlags } = params;

  if (!subscriptions.CAN_ACCESS_COMPTRYX) return false;
  if (!featureFlags?.CAN_ACCESS_COMPTRYX) return false;

  return true;
};

export const canSeeComptryxMarketData = (params: PermissionsParams): boolean => {
  const { featureFlags } = params;

  if (!featureFlags?.CAN_ACCESS_COMPTRYX) return false;

  return createAbility(params.user, params).can(Action.Access, Subject.MarketData_Comptryx);
};

export const canAccessComptryxMarketData = (params: PermissionsParams): boolean => {
  const { featureFlags, subscriptions } = params;

  if (!subscriptions.CAN_ACCESS_COMPTRYX) return false;
  if (!featureFlags?.CAN_ACCESS_COMPTRYX) return false;

  return createAbility(params.user, params).can(Action.Access, Subject.MarketData_Comptryx);
};

export const canAccessCompanyDashboard = (params: PermissionsParams): boolean => {
  const { user } = params;

  if (!user?.company.hasImportedEmployees) {
    return false;
  }

  return createAbility(user, params).can(Action.Access, Subject.CompanyDashboard);
};

export const canAccessCompanyDashboardDetails = (params: PermissionsParams): boolean => {
  if (!canAccessCompanyDashboard(params)) {
    return false;
  }

  const roles = getUserRoles(params.user);
  if (!roles.isAdmin) {
    return false;
  }

  return true;
};

export const canAccessPeopleDashboard = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.PeopleDashboard);
};

export const canAccessMarketDataPeopleDashboard = (params: PermissionsParams): boolean => {
  const { subscriptions, user } = params;

  const canAccessPeopleDashboard = createAbility(user, params).can(Action.Access, Subject.PeopleDashboard);

  if (!subscriptions.CAN_ACCESS_BENCHMARK && !canAccessPeopleDashboard) {
    return false;
  }

  if (params.featureFlags?.CANNOT_ACCESS_PEOPLE_MARKET_POSITIONING) {
    return false;
  }

  return createAbility(user, params).can(Action.Access, Subject.PeopleDashboard_MarketData);
};

export const canAccessComptryxMarketDataPeopleDashboard = (params: PermissionsParams): boolean => {
  const { featureFlags, subscriptions } = params;

  if (!subscriptions.CAN_ACCESS_COMPTRYX) return false;
  if (!featureFlags?.CAN_ACCESS_COMPTRYX) return false;

  return createAbility(params.user, params).can(Action.Access, Subject.PeopleDashboard_Comptryx);
};

export const canAccessSalaryBandsPeopleDashboard = (params: PermissionsParams): boolean => {
  const { subscriptions, user } = params;

  const canAccessPeopleDashboard = createAbility(user, params).can(Action.Access, Subject.PeopleDashboard);

  //not access to the module
  if (!canAccessPeopleDashboard) {
    return false;
  }

  // not access to salary band or doesn't have a live grid
  if (!subscriptions.CAN_ACCESS_SALARY_BANDS && !user?.company.defaultSalaryGridId) {
    return false;
  }

  // if it would be empty
  if (!user?.company.hasImportedEmployees) {
    return false;
  }

  return createAbility(params.user, params).can(Action.Access, Subject.PeopleDashboard_SalaryBands);
};

export const canAccessRawData = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.RawData);
};

export const canAccessIntegrations = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.Integrations);
};

export const canAccessSettings = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.Settings);
};

// noinspection JSUnusedGlobalSymbols
export const canAccessImportedEmployees = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.ImportedEmployees);
};

export const canAccessAccount = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.Account);
};

export const canAccessFileManager = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Access, Subject.FileManager);
};

export const canAccessGenderPayGapReport = (params: PermissionsParams): boolean => {
  const { featureFlags, user } = params;

  if (!featureFlags.CAN_ACCESS_GENDER_PAY_GAP_REPORT) {
    return false;
  }

  return createAbility(user, params).can(Action.Access, Subject.GenderPayGapReport);
};

export const canAccessPermissionsCenter = (params: PermissionsParams): boolean => {
  return createAbility(params.user, params).can(Action.Update, Subject.Users);
};
